import React, { useEffect, useState, useCallback } from "react";
import { apiGet } from "../../../services/api";
import ConsultantPaymentModal from "./ConsultantPaymentModal";
import { useToast } from "../../common/ToastProvider";
import Button from "../../common/Button";
import "./consultants.css";

interface LedgerRow {
  id: number;
  type?: string;
  date?: string;
  patient?: string;
  patient_id?: string;
  tooth_no?: string;
  procedure?: string;
  fee?: number;
  paid_amount?: number;
  balance?: number;
  paid?: boolean;
  consultant_name?: string;
}

interface ConsultantLedgerProps {
  consultant: { name?: string; id?: number } | string | null;
}

export default function ConsultantLedger({ consultant }: ConsultantLedgerProps) {
  const { showToast } = useToast();
  const [rows, setRows] = useState<LedgerRow[]>([]);
  const [payingEntry, setPayingEntry] = useState<LedgerRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<{ id: string; name?: string } | null>(null);
  const [patientPayments, setPatientPayments] = useState<{ id: number; bill_id: number; amount: number; date: string; mode?: string }[]>([]);

  const [filterMonth, setFilterMonth] = useState(
    new Date().toISOString().slice(0, 7)
  );

  const consultantName = (consultant as { name?: string })?.name ?? (typeof consultant === "string" ? consultant : "");

  const loadLedger = useCallback(() => {
    if (!consultantName) return;

    setLoading(true);
    apiGet(`/api/manager/consultants/${encodeURIComponent(consultantName)}/ledger`)
      .then((data: unknown) => {
        setRows(Array.isArray(data) ? (data as LedgerRow[]) : []);
      })
      .catch((err) => {
        showToast({ message: "Failed to load consultant ledger.", type: "error" });
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, [consultantName, showToast]);

  useEffect(() => {
    loadLedger();
  }, [loadLedger]);

  useEffect(() => {
    if (!selectedPatient?.id) return;
    apiGet(`/api/manager/patient-records/${selectedPatient.id}`)
      .then((value: unknown) => {
        const data = value as { payments?: { id: number; bill_id: number; amount: number; date: string; mode?: string }[] };
        setPatientPayments(Array.isArray(data.payments) ? data.payments : []);
      })
      .catch(() => setPatientPayments([]));
  }, [selectedPatient]);

  if (!consultantName) {
    return (
      <div className="mod-empty-state" style={{ height: '300px', border: '1px dashed #cbd5e1' }}>
        <span style={{ fontSize: '2rem' }}>👈</span>
        <p style={{ marginTop: '8px' }}>Select a consultant to view their settlement ledger.</p>
      </div>
    );
  }

  const filteredRows = rows.filter((r) =>
    r.date?.startsWith(filterMonth)
  );

  const totalEarnings = filteredRows.reduce(
    (sum, r) => sum + (r.fee || 0),
    0
  );

  const totalPaid = filteredRows.reduce(
    (sum, r) => sum + (r.paid_amount || 0),
    0
  );

  const totalPending = filteredRows.reduce(
    (sum, r) => sum + (r.balance || 0),
    0
  );

  return (
    <div className="mod-standalone-window" style={{ minHeight: 'calc(100vh - 100px)' }}>
      <div className="mod-window-header">
        <div>
          <h2>👨⚕️ {consultantName}</h2>
          <p className="mod-text-muted">Track consultant earnings, pending payouts, and clinical splits.</p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <input
            type="month"
            value={filterMonth}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterMonth(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem' }}
          />
          <Button variant="outline" onClick={loadLedger} isLoading={loading}>Refresh Ledger</Button>
        </div>
      </div>

      <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
        <div style={{ background: '#fef2f2', padding: '16px', borderRadius: '12px', border: '1px solid #fecaca' }}>
          <div style={{ fontSize: '0.75rem', color: '#991b1b', textTransform: 'uppercase', fontWeight: 700 }}>Pending Due</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#dc2626' }}>₹{totalPending.toLocaleString()}</div>
        </div>
        <div style={{ background: '#f0fdf4', padding: '16px', borderRadius: '12px', border: '1px solid #bbf7d0' }}>
          <div style={{ fontSize: '0.75rem', color: '#166534', textTransform: 'uppercase', fontWeight: 700 }}>Total Paid</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#15803d' }}>₹{totalPaid.toLocaleString()}</div>
        </div>
        <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Total Earnings</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f172a' }}>₹{totalEarnings.toLocaleString()}</div>
        </div>
      </div>

      <div style={{ overflowX: 'auto', padding: '16px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', textAlign: 'left' }}>
          <thead style={{ borderBottom: '2px solid #e2e8f0', color: '#64748b' }}>
            <tr>
              <th style={{ padding: '12px 8px' }}>Date</th>
              <th style={{ padding: '12px 8px' }}>Patient</th>
              <th style={{ padding: '12px 8px' }}>Procedure</th>
              <th style={{ padding: '12px 8px', textAlign: 'right' }}>Fee</th>
              <th style={{ padding: '12px 8px', textAlign: 'right' }}>Paid</th>
              <th style={{ padding: '12px 8px', textAlign: 'right' }}>Balance</th>
              <th style={{ padding: '12px 8px', textAlign: 'center' }}>Status</th>
              <th style={{ padding: '12px 8px', textAlign: 'right' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>Loading ledger...</td></tr>
            ) : filteredRows.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>No entries for this month</td></tr>
            ) : (
              filteredRows.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '12px 8px', color: '#64748b' }}>
                    {r.date ? new Date(r.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—"}
                  </td>
                  <td style={{ padding: '12px 8px' }}>
                    {r.patient_id ? (
                      <span
                        style={{ color: '#0ea5e9', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline' }}
                        onClick={() => setSelectedPatient({ id: String(r.patient_id), name: r.patient || "" })}
                      >
                        {r.patient || r.patient_id}
                      </span>
                    ) : (
                      <span style={{ fontWeight: 600 }}>{r.patient || "—"}</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 8px' }}>
                    <div style={{ fontWeight: 600, color: '#334155' }}>{r.procedure}</div>
                    {r.tooth_no && <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Tooth: {r.tooth_no}</div>}
                  </td>
                  <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600 }}>₹{r.fee}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'right', color: '#16a34a' }}>₹{r.paid_amount ?? 0}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'right', color: (r.balance ?? 0) > 0 ? '#dc2626' : '#166534', fontWeight: 700 }}>
                    ₹{r.balance ?? 0}
                  </td>
                  <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                    <span style={{ 
                      background: r.paid ? '#dcfce7' : '#fef3c7', 
                      color: r.paid ? '#166534' : '#d97706', 
                      padding: '4px 10px', 
                      borderRadius: '20px', 
                      fontSize: '0.75rem', 
                      fontWeight: 700 
                    }}>
                      {r.paid ? "PAID" : "DUE"}
                    </span>
                  </td>
                  <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                    {(r.balance ?? 0) > 0 ? (
                      <Button variant="primary" size="sm" onClick={() => setPayingEntry(r)}>Pay</Button>
                    ) : (
                      <span style={{ color: '#16a34a', fontSize: '0.85rem', fontWeight: 600 }}>✓ Settled</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Patient Payment History Modal */}
      {selectedPatient && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setSelectedPatient(null)}>
          <div style={{ width: '500px', background: '#fff', padding: '24px', borderRadius: '16px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Payment History</h3>
              <button 
                onClick={() => setSelectedPatient(null)} 
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#64748b' }}
              >
                &times;
              </button>
            </div>
            <p style={{ margin: '0 0 20px 0', fontSize: '0.9rem', color: '#64748b' }}>
              {selectedPatient.name || selectedPatient.id}
            </p>

            <div style={{ overflowY: 'auto', flex: 1 }}>
              {patientPayments.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>No payments found.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', textAlign: 'left' }}>
                  <thead style={{ borderBottom: '2px solid #e2e8f0', color: '#64748b' }}>
                    <tr>
                      <th style={{ padding: '12px 8px' }}>Date</th>
                      <th style={{ padding: '12px 8px' }}>Bill</th>
                      <th style={{ padding: '12px 8px' }}>Mode</th>
                      <th style={{ padding: '12px 8px', textAlign: 'right' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {patientPayments.map((p) => (
                      <tr key={p.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '12px 8px' }}>{p.date || "—"}</td>
                        <td style={{ padding: '12px 8px' }}>#{p.bill_id}</td>
                        <td style={{ padding: '12px 8px' }}>{p.mode || "—"}</td>
                        <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600, color: '#16a34a' }}>₹{p.amount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
               <Button variant="outline" onClick={() => setSelectedPatient(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}

      {payingEntry && (
        <ConsultantPaymentModal
          entry={payingEntry}
          onClose={() => setPayingEntry(null)}
          onPaid={() => {
            setPayingEntry(null);
            loadLedger();
          }}
        />
      )}
    </div>
  );
}
